import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';

const values = [
  {
    icon: 'ri-focus-3-line',
    title: '信息聚合',
    desc: '把分散在各处的赛事信息集中呈现，让关注比赛的人快速掌握当天重点。',
  },
  {
    icon: 'ri-database-2-line',
    title: '数据可读',
    desc: '黑5数据不只是数字，我们把它翻译成趋势、对比和结论，方便理解。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '中立客观',
    desc: '所有分析基于公开数据与统计方法，不构成任何投注建议。',
  },
  {
    icon: 'ri-speed-up-line',
    title: '更新及时',
    desc: '焦点赛事与最新资讯按时间倒序排列，保证信息的时效性。',
  },
];

const milestones = [
  { year: '2023', title: '平台起步', desc: '从单一赛事资讯聚合开始，逐步建立赛事分类与资讯更新机制。' },
  { year: '2024', title: '黑5数据体系', desc: '上线黑5综合指数与关键指标对比，形成可回溯的数据归档能力。' },
  { year: '2025', title: '深度分析专题', desc: '推出数据分析专题栏目，覆盖足球、篮球、电竞等主流项目。' },
  { year: '2026', title: '全站体验升级', desc: '完善搜索、筛选与移动端适配，提升信息获取效率。' },
];

const team = [
  { name: '赛事数据组', role: '数据采集与统计', desc: '负责赛事数据采集、清洗与指标计算，维护黑5数据体系。' },
  { name: '内容分析组', role: '赛事解读与分析', desc: '撰写赛事简介、分析报告与专题内容，输出可读性强的结论。' },
  { name: '产品体验组', role: '产品与体验设计', desc: '负责信息架构、页面体验与移动端适配，保证阅读流畅。' },
];

export default function AboutPage() {
  usePageMeta({
    title: '关于我们 - 51每日大赛',
    description:
      '了解51每日大赛的品牌定位、团队构成、发展历程与合作伙伴。我们专注赛事资讯聚合与往期黑5数据分析，为体育爱好者提供清晰、中立的信息参考。',
    keywords: '关于我们,51每日大赛,赛事资讯平台,黑5数据,团队介绍',
  });

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        <div className="mt-4 bg-primary-900 rounded-lg relative overflow-hidden px-5 md:px-8 py-10 md:py-14">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-accent-500/15" />
          <div className="absolute -left-10 bottom-0 w-48 h-48 rounded-full bg-secondary-500/10" />
          <div className="relative max-w-3xl">
            <span className="inline-block text-xs font-medium tracking-widest text-accent-400 uppercase mb-2">
              About Us
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
              51每日大赛 · 今日大赛聚集地
            </h1>
            <p className="mt-4 text-sm md:text-base text-primary-200 leading-relaxed">
              我们是一个聚焦综合赛事资讯与黑5数据分析的信息平台。目标用户包括体育赛事关注者、赛事分析人员
              以及希望了解赛事全貌的普通观众——让大家能在同一个地方，快速获取当天及历史赛事信息。
            </p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-10 md:py-12">
        <section>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5">
            我们的坚持
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((item) => (
              <div key={item.title} className="bg-background-50 border border-background-200 rounded-lg p-5">
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className={`${item.icon} w-5 h-5 flex items-center justify-center text-lg`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5">
            发展历程
          </h2>
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-7">
            <ol className="relative border-l border-background-300 ml-2 flex flex-col gap-7">
              {milestones.map((item, index) => (
                <li key={item.year} className="pl-6 relative">
                  <span
                    className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-background-50 ${
                      index === milestones.length - 1 ? 'bg-accent-500' : 'bg-primary-500'
                    }`}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-sm font-semibold text-accent-600">{item.year}</span>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  </div>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5">
            团队构成
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {team.map((item) => (
              <div key={item.name} className="bg-background-50 border border-background-200 rounded-lg p-5">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center">
                    <i className="ri-group-line w-5 h-5 flex items-center justify-center text-lg" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{item.name}</h3>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5">
            合作伙伴
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {['赛事数据源', '体育媒体', '场馆合作', '数据服务商', '内容共创', '开发者社区'].map((name) => (
              <div
                key={name}
                className="bg-background-100 border border-background-200 rounded-lg py-6 flex flex-col items-center gap-2"
              >
                <i className="ri-building-2-line text-primary-600 w-6 h-6 flex items-center justify-center text-xl" />
                <span className="text-xs text-foreground-600">{name}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2 mb-3">
              <i className="ri-copyright-line text-primary-600 w-5 h-5 flex items-center justify-center" />
              版权声明
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              本站所有内容（包括文字、数据与图表）均为演示示例，仅用于信息展示与功能说明，不构成任何投注或投资建议。
              未经授权，不得将本站内容用于商业用途。如需转载或合作，请通过联系我们页面取得联系。
            </p>
          </div>
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2 mb-3">
              <i className="ri-file-list-3-line text-primary-600 w-5 h-5 flex items-center justify-center" />
              服务条款
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              使用本站即表示您理解并接受：本站提供的信息仅供参考，用户应自行判断信息的准确性与适用性。
              本站不对因使用信息而产生的任何直接或间接损失负责。我们保留随时调整内容与服务的权利。
            </p>
          </div>
        </section>

        <section className="mt-8 rounded-lg bg-secondary-50 border border-secondary-200 p-5 md:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-base md:text-lg font-semibold text-secondary-900">
              对平台有建议或合作意向？
            </h2>
            <p className="mt-1 text-sm text-secondary-800">欢迎随时联系我们，我们会认真阅读每一条反馈。</p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-700 text-background-50 text-sm hover:bg-primary-800 transition-colors whitespace-nowrap"
          >
            <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center" />
            前往联系我们
          </Link>
        </section>
      </div>
    </div>
  );
}